// Primary 3 English Learning Data - Part 1 (Topics 1-10)
// Based on Primary English Learning P3 Topics.txt

export const p3DataPart1 = {
  gradeId: 'p3',
  gradeName: 'Primary 3 (小學三年級)',
  objectives: [
    'Build on Primary 2 foundations with more advanced grammar structures',
    'Develop reading comprehension and writing skills with complex texts',
    'Learn past tense and continuous forms',
    'Master phrasal verbs and infinitives',
    'Understand sequence markers and time expressions'
  ],
  keySkills: [
    'Simple past tense and past continuous',
    'Phrasal verbs and infinitives',
    'Sequence markers and time expressions',
    'Modal verbs and adverbs of degree',
    'Complex sentence structures'
  ],
  topics: [
    {
      id: 'p3-topic-1',
      title: 'Imperatives',
      description: 'Learning to give commands and instructions using imperative sentences with more complexity',
      purpose: 'To teach students how to form and use imperative sentences to give commands, instructions, or make requests with more variety and complexity than previous grades.',
      questions: [
        {
          question: 'Complete the command: "_____ the door, please."',
          answer: 'Open',
          explanation: 'Imperatives start with the base form of the verb (no subject needed).'
        },
        {
          question: 'Fill in the blank: "_____ your homework now."',
          answer: 'Do',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ quiet in the library."',
          answer: 'Be',
          explanation: 'Use "be" as the base form for the verb "to be" in imperatives.'
        },
        {
          question: 'Fill in the blank: "_____ your hands before eating."',
          answer: 'Wash',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the window, it\'s cold."',
          answer: 'Close',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your teeth twice a day."',
          answer: 'Brush',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ to the teacher."',
          answer: 'Listen',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your room every day."',
          answer: 'Clean',
          explanation: 'Use the base form of the verb for imperatives.'
        },
        {
          question: 'Complete: "_____ the ball to me."',
          answer: 'Throw',
          explanation: 'Imperatives start with the base form of the verb.'
        },
        {
          question: 'Fill in the blank: "_____ your shoes before entering."',
          answer: 'Take off',
          explanation: 'Use the base form of the verb for imperatives.'
        }
      ]
    },
    {
      id: 'p3-topic-2',
      title: 'Verbs with Prepositions',
      description: 'Learning to use verbs that require specific prepositions',
      purpose: 'To teach students how to use verbs that are followed by specific prepositions to express different meanings and relationships.',
      questions: [
        {
          question: 'I listen _____ music.',
          answer: 'to',
          explanation: 'Use "to" after "listen" when listening to something.'
        },
        {
          question: 'She looks _____ her keys.',
          answer: 'for',
          explanation: 'Use "for" after "look" when searching for something.'
        },
        {
          question: 'He waits _____ the bus.',
          answer: 'for',
          explanation: 'Use "for" after "wait" when waiting for something or someone.'
        },
        {
          question: 'I think _____ my family.',
          answer: 'about',
          explanation: 'Use "about" after "think" when considering or contemplating something.'
        },
        {
          question: 'She talks _____ her friends.',
          answer: 'to',
          explanation: 'Use "to" after "talk" when speaking to someone.'
        },
        {
          question: 'He looks _____ the window.',
          answer: 'out of',
          explanation: 'Use "out of" after "look" when looking through something.'
        },
        {
          question: 'I care _____ my pets.',
          answer: 'about',
          explanation: 'Use "about" after "care" when showing concern or interest.'
        },
        {
          question: 'She laughs _____ the joke.',
          answer: 'at',
          explanation: 'Use "at" after "laugh" when laughing at something.'
        },
        {
          question: 'He smiles _____ me.',
          answer: 'at',
          explanation: 'Use "at" after "smile" when smiling at someone.'
        },
        {
          question: 'I dream _____ becoming a teacher.',
          answer: 'about',
          explanation: 'Use "about" after "dream" when dreaming of something.'
        }
      ]
    },
    {
      id: 'p3-topic-3',
      title: 'Simple Past Tense',
      description: 'Learning to use simple past tense to describe completed actions in the past',
      purpose: 'To teach students how to form and use simple past tense to describe actions that happened and were completed in the past.',
      questions: [
        {
          question: 'I _____ to school yesterday.',
          answer: 'went',
          explanation: 'Use the past form of "go" for completed actions in the past.'
        },
        {
          question: 'She _____ a book last night.',
          answer: 'read',
          explanation: 'Use the past form of "read" for completed actions in the past.'
        },
        {
          question: 'They _____ football yesterday.',
          answer: 'played',
          explanation: 'Use the past form of "play" for completed actions in the past.'
        },
        {
          question: 'He _____ in Hong Kong last year.',
          answer: 'lived',
          explanation: 'Use the past form of "live" for completed actions in the past.'
        },
        {
          question: 'We _____ dinner at 7 PM.',
          answer: 'ate',
          explanation: 'Use the past form of "eat" for completed actions in the past.'
        },
        {
          question: 'The sun _____ at 6 AM.',
          answer: 'rose',
          explanation: 'Use the past form of "rise" for completed actions in the past.'
        },
        {
          question: 'I _____ my teeth this morning.',
          answer: 'brushed',
          explanation: 'Use the past form of "brush" for completed actions in the past.'
        },
        {
          question: 'She _____ to music yesterday.',
          answer: 'listened',
          explanation: 'Use the past form of "listen" for completed actions in the past.'
        },
        {
          question: 'They _____ Chinese at home.',
          answer: 'spoke',
          explanation: 'Use the past form of "speak" for completed actions in the past.'
        },
        {
          question: 'He _____ basketball after school.',
          answer: 'played',
          explanation: 'Use the past form of "play" for completed actions in the past.'
        }
      ]
    },
    {
      id: 'p3-topic-4',
      title: 'Is/Are there...?',
      description: 'Learning to ask questions about existence using "Is there" and "Are there"',
      purpose: 'To teach students how to form questions to ask about the existence of people, places, or things.',
      questions: [
        {
          question: '_____ a cat in the garden?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any books on the table?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a school near here?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ many students in the class?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a park in this area?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any shops nearby?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a library in the town?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ many cars on the road?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        },
        {
          question: '_____ a hospital in the city?',
          answer: 'Is there',
          explanation: 'Use "Is there" to ask about the existence of a singular thing.'
        },
        {
          question: '_____ any trees in the park?',
          answer: 'Are there',
          explanation: 'Use "Are there" to ask about the existence of plural things.'
        }
      ]
    },
    {
      id: 'p3-topic-5',
      title: 'Prepositions of Time',
      description: 'Learning to use prepositions of time (at, in, on, before, after, during)',
      purpose: 'To teach students how to use prepositions to describe when events happen or the timing of actions.',
      questions: [
        {
          question: 'I go to school _____ 8 o\'clock.',
          answer: 'at',
          explanation: 'Use "at" for specific times.'
        },
        {
          question: 'I was born _____ 2010.',
          answer: 'in',
          explanation: 'Use "in" for years, months, and seasons.'
        },
        {
          question: 'I have a party _____ Saturday.',
          answer: 'on',
          explanation: 'Use "on" for days of the week and dates.'
        },
        {
          question: 'I eat breakfast _____ school.',
          answer: 'before',
          explanation: 'Use "before" to show that one action happens earlier than another.'
        },
        {
          question: 'I go home _____ school.',
          answer: 'after',
          explanation: 'Use "after" to show that one action happens later than another.'
        },
        {
          question: 'I sleep _____ night.',
          answer: 'at',
          explanation: 'Use "at" for specific times like "at night".'
        },
        {
          question: 'I go on holiday _____ summer.',
          answer: 'in',
          explanation: 'Use "in" for seasons.'
        },
        {
          question: 'I have a test _____ Monday.',
          answer: 'on',
          explanation: 'Use "on" for days of the week.'
        },
        {
          question: 'I study _____ the exam.',
          answer: 'before',
          explanation: 'Use "before" to show that one action happens earlier than another.'
        },
        {
          question: 'I play games _____ homework.',
          answer: 'after',
          explanation: 'Use "after" to show that one action happens later than another.'
        }
      ]
    },
    {
      id: 'p3-topic-6',
      title: 'Telling the Time',
      description: 'Learning to tell time using o\'clock, half past, quarter past, quarter to',
      purpose: 'To teach students how to read and tell time using different time expressions.',
      questions: [
        {
          question: 'What time is it when the clock shows 3:00?',
          answer: 'three o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 2:30?',
          answer: 'half past two',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 1:15?',
          answer: 'quarter past one',
          explanation: 'Use "quarter past" for 15 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 4:45?',
          answer: 'quarter to five',
          explanation: 'Use "quarter to" for 15 minutes before the next hour.'
        },
        {
          question: 'What time is it when the clock shows 6:00?',
          answer: 'six o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 5:30?',
          answer: 'half past five',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 7:15?',
          answer: 'quarter past seven',
          explanation: 'Use "quarter past" for 15 minutes past the hour.'
        },
        {
          question: 'What time is it when the clock shows 8:45?',
          answer: 'quarter to nine',
          explanation: 'Use "quarter to" for 15 minutes before the next hour.'
        },
        {
          question: 'What time is it when the clock shows 9:00?',
          answer: 'nine o\'clock',
          explanation: 'Use "o\'clock" for exact hours.'
        },
        {
          question: 'What time is it when the clock shows 10:30?',
          answer: 'half past ten',
          explanation: 'Use "half past" for 30 minutes past the hour.'
        }
      ]
    },
    {
      id: 'p3-topic-7',
      title: 'Quantifiers',
      description: 'Learning to use quantifiers (some, any, many, much, a few, a little, lots of) with countable and uncountable nouns',
      purpose: 'To teach students how to use different quantifiers to express amounts and quantities with both countable and uncountable nouns.',
      questions: [
        {
          question: 'I have _____ apples.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with countable nouns.'
        },
        {
          question: 'Do you have _____ water?',
          answer: 'any',
          explanation: 'Use "any" for questions and negative statements with uncountable nouns.'
        },
        {
          question: 'There are _____ books on the shelf.',
          answer: 'many',
          explanation: 'Use "many" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ money.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        },
        {
          question: 'I have _____ friends.',
          answer: 'a few',
          explanation: 'Use "a few" with countable nouns to show a small quantity.'
        },
        {
          question: 'There is _____ sugar in the bowl.',
          answer: 'some',
          explanation: 'Use "some" for positive statements with uncountable nouns.'
        },
        {
          question: 'Do you have _____ pencils?',
          answer: 'any',
          explanation: 'Use "any" for questions with countable nouns.'
        },
        {
          question: 'I have _____ time.',
          answer: 'a little',
          explanation: 'Use "a little" with uncountable nouns to show a small quantity.'
        },
        {
          question: 'There are _____ students in the class.',
          answer: 'lots of',
          explanation: 'Use "lots of" with countable nouns to show a large quantity.'
        },
        {
          question: 'I don\'t have _____ homework.',
          answer: 'much',
          explanation: 'Use "much" with uncountable nouns in negative statements.'
        }
      ]
    },
    {
      id: 'p3-topic-8',
      title: 'Conjunctions',
      description: 'Learning to connect words, phrases, and sentences using conjunctions',
      purpose: 'To teach students how to use basic conjunctions like "and", "but", "or", "so", "because" to connect ideas and create compound sentences.',
      questions: [
        {
          question: 'Complete: "I like apples _____ oranges."',
          answer: 'and',
          explanation: 'Use "and" to connect similar ideas or add information.'
        },
        {
          question: 'Fill in the blank: "I am tired _____ I want to sleep."',
          answer: 'so',
          explanation: 'Use "so" to show cause and effect relationship.'
        },
        {
          question: 'Choose the correct conjunction: "I like tea _____ I don\'t like coffee."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between two ideas.'
        },
        {
          question: 'Complete: "Do you want tea _____ coffee?"',
          answer: 'or',
          explanation: 'Use "or" to show a choice between two options.'
        },
        {
          question: 'Fill in the blank: "I am hungry _____ I will eat."',
          answer: 'so',
          explanation: 'Use "so" to show the result of being hungry.'
        },
        {
          question: 'Choose the correct conjunction: "I like cats _____ dogs."',
          answer: 'and',
          explanation: 'Use "and" to connect two things you like.'
        },
        {
          question: 'Complete: "It is raining _____ I will stay home."',
          answer: 'so',
          explanation: 'Use "so" to show the result of rain.'
        },
        {
          question: 'Fill in the blank: "I can swim _____ I can\'t dive."',
          answer: 'but',
          explanation: 'Use "but" to show contrast between abilities.'
        },
        {
          question: 'Choose the correct conjunction: "Would you like milk _____ juice?"',
          answer: 'or',
          explanation: 'Use "or" to offer a choice between two drinks.'
        },
        {
          question: 'Complete: "I am happy _____ I got a new toy."',
          answer: 'because',
          explanation: 'Use "because" to explain the reason for being happy.'
        }
      ]
    },
    {
      id: 'p3-topic-9',
      title: 'Pass Continuous Tense',
      description: 'Learning to use past continuous tense to describe actions happening at a specific time in the past',
      purpose: 'To teach students how to form and use past continuous tense (was/were + verb-ing) to describe actions that were in progress at a specific time in the past.',
      questions: [
        {
          question: 'I _____ reading a book when you called.',
          answer: 'was',
          explanation: 'Use "was" with "I" in past continuous tense.'
        },
        {
          question: 'She _____ playing in the park yesterday.',
          answer: 'was',
          explanation: 'Use "was" with singular subjects in past continuous.'
        },
        {
          question: 'They _____ eating lunch at 12 PM.',
          answer: 'were',
          explanation: 'Use "were" with plural subjects in past continuous.'
        },
        {
          question: 'The cat _____ sleeping when I arrived.',
          answer: 'was',
          explanation: 'Use "was" with "it" (the cat) in past continuous.'
        },
        {
          question: 'We _____ watching TV last night.',
          answer: 'were',
          explanation: 'Use "were" with "we" in past continuous tense.'
        },
        {
          question: 'He _____ running in the morning.',
          answer: 'was',
          explanation: 'Use "was" with "he" in past continuous tense.'
        },
        {
          question: 'You _____ studying English yesterday.',
          answer: 'were',
          explanation: 'Use "were" with "you" in past continuous.'
        },
        {
          question: 'The birds _____ singing in the trees.',
          answer: 'were',
          explanation: 'Use "were" with plural subjects (birds) in past continuous.'
        },
        {
          question: 'It _____ raining all day yesterday.',
          answer: 'was',
          explanation: 'Use "was" with "it" in past continuous tense.'
        },
        {
          question: 'I _____ writing a letter when the phone rang.',
          answer: 'was',
          explanation: 'Use "was" with "I" in past continuous tense.'
        }
      ]
    },
    {
      id: 'p3-topic-10',
      title: 'Question Words',
      description: 'Learning to use question words (what, where, when, who, why, how) in more complex sentences',
      purpose: 'To teach students how to form questions using different question words to gather specific information in more complex contexts.',
      questions: [
        {
          question: '_____ is your name?',
          answer: 'What',
          explanation: 'Use "What" to ask about things, names, or objects.'
        },
        {
          question: '_____ do you live?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location or place.'
        },
        {
          question: '_____ do you go to school?',
          answer: 'When',
          explanation: 'Use "When" to ask about time or schedule.'
        },
        {
          question: '_____ is your best friend?',
          answer: 'Who',
          explanation: 'Use "Who" to ask about people.'
        },
        {
          question: '_____ do you like ice cream?',
          answer: 'Why',
          explanation: 'Use "Why" to ask for reasons or explanations.'
        },
        {
          question: '_____ old are you?',
          answer: 'How',
          explanation: 'Use "How" to ask about quantity, age, or manner.'
        },
        {
          question: '_____ is your school?',
          answer: 'Where',
          explanation: 'Use "Where" to ask about location.'
        },
        {
          question: '_____ do you eat breakfast?',
          answer: 'When',
          explanation: 'Use "When" to ask about time.'
        },
        {
          question: '_____ is your favorite subject?',
          answer: 'What',
          explanation: 'Use "What" to ask about preferences or choices.'
        },
        {
          question: '_____ do you go to school?',
          answer: 'How',
          explanation: 'Use "How" to ask about the way or method.'
        }
      ]
    }
  ]
}; 